import React, { useEffect, useState } from 'react';
import { useAddressBookStore } from '@/stores/agents/address-book-store';
import { AddressBookEntry } from '@/services/api/agentApi';
import { Users, Bot, Server, Shield, Trash2, Plus } from 'lucide-react';

const contactTypeIcons: Record<string, React.ReactNode> = {
  user: <Users className="w-4 h-4" />,
  agent: <Bot className="w-4 h-4" />,
  system: <Server className="w-4 h-4" />,
};

const priorityColors: Record<string, string> = {
  always: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'waking-hours': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  'business-hours': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  never: 'bg-muted text-muted-foreground',
  'context-dependent': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

export function AddressBookTab() {
  const { entries, isLoading, error, fetchEntries, deleteEntry, seedFromConnections } = useAddressBookStore();
  const [showAdd, setShowAdd] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  if (isLoading && entries.length === 0) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading address book...</div>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Address Book</h2>
          <p className="text-sm text-muted-foreground">Contacts, interrupt rules, and escalation settings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => seedFromConnections()}
            className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent"
          >
            Import Connections
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" /> Add Contact
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">{error}</div>}

      {entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-sm">No contacts yet. Import from your connections or add manually.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <div key={entry.entry_id} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg hover:border-foreground/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-full">
                  {contactTypeIcons[entry.contact_type] || <Users className="w-4 h-4" />}
                </div>
                <div>
                  <div className="font-medium text-foreground">{entry.contact_name}</div>
                  {entry.relationship_context && (
                    <div className="text-xs text-muted-foreground">{entry.relationship_context}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs rounded-full ${priorityColors[entry.interrupt_priority] || 'bg-muted text-muted-foreground'}`}>
                  {entry.interrupt_priority}
                </span>
                {entry.dnd_override && (
                  <Shield className="w-4 h-4 text-destructive" aria-label="Can break through DND" />
                )}
                {entry.escalation_steps.length > 0 && (
                  <span className="text-xs text-muted-foreground/70">{entry.escalation_steps.length} escalation steps</span>
                )}
                {confirmingDelete === entry.contact_id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { deleteEntry(entry.contact_id); setConfirmingDelete(null); }}
                      className="text-xs text-destructive hover:text-destructive/80"
                    >
                      Delete?
                    </button>
                    <button
                      onClick={() => setConfirmingDelete(null)}
                      className="text-xs text-muted-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingDelete(entry.contact_id)}
                    className="p-1 text-muted-foreground/70 hover:text-destructive"
                    aria-label={`Delete ${entry.contact_name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
